import React, { useState } from 'react';
import axios from 'axios';
import { useFormik } from 'formik';
import Helmet from 'react-helmet';
import Input from '../components/form/FormikInput';
import { ReactComponent as PassportLogo } from '../svg/passport_logo.svg';
import { LoginFooter, passwordSchema } from './Login';
import { useHistory } from 'react-router-dom';
import { useQueryStringParam } from '../utils/routing';
import { object, string } from 'yup';

const forgotPassword = (email, password, code) => {
    return axios.request({
        url: `${process.env.REACT_APP_API_URL}/unauthorized/confirmforgotpassword`,
        method: 'POST',
        data: {
            username: email,
            password,
            confirmationcode: code
        }
    });
};

const ResetPassword = () => {
    const email = useQueryStringParam('email');
    const [error, setError] = useState(null);
    const [valid, setValid] = useState([]);
    const history = useHistory();
    const { values, errors, touched, isSubmitting, handleSubmit, handleChange } = useFormik({
        initialValues: {
            email: email || '',
            password: '',
            code: ''
        },
        validationSchema: object().shape({
            email: string().label('Email').email().required(),
            password: string()
                .label('Password')
                .required()
                .test('meets-reqs', 'Password does not meet the minimum password requirements.', (value) => {
                    const isValid = passwordSchema.validate(value || '');
                    return isValid;
                }),
            code: string()
                .label('Code')
                .required()
                .test(
                    'is-code',
                    'Code should only include digits. Remove any alphabet, punctuation, or white-space characters.',
                    (value, context) => {
                        const regex = /^(\d+)$/gm;
                        return regex.test(value);
                    }
                )
        }),
        onSubmit: async (values) => {
            try {
                await forgotPassword(values.email, values.password, values.code.trim());
                history.push({ pathname: '/login' });
            } catch (err) {
                console.error(err);
                if (err.response.data.message === 'ExpiredCodeException') {
                    setError('Verification Code Expired.');
                }
            }
        }
    });

    React.useEffect(() => {
        setValid(passwordSchema.validate(values.password, { list: true }));
    }, [values.password]);

    return (
        <div className="page-signin">
            <Helmet>
                <title>Login</title>
            </Helmet>
            <PassportLogo className="logo" width="150" />

            <div className="row mb-5">
                <form className="col-xs-12 col-md-3 col-md-offset-4" onSubmit={handleSubmit} noValidate>
                    <p>Please enter your new password and the confirmation code we emailed to you.</p>
                    <Input
                        label="Email"
                        type="email"
                        id="email"
                        value={values.email}
                        errors={errors.email}
                        touched={touched.email}
                        autoComplete="email"
                        required
                        autoFocus
                        onChange={handleChange}
                    />
                    <Input
                        label="Verification Code"
                        type="code"
                        id="code"
                        value={values.code}
                        errors={errors.code}
                        touched={touched.code}
                        required
                        autoFocus
                        onChange={handleChange}
                    />
                    <Input
                        label="New Password"
                        type="password"
                        id="password"
                        value={values.password}
                        errors={errors.password}
                        touched={touched.password}
                        autoComplete="new-password"
                        required
                        autoFocus
                        onChange={handleChange}
                    />

                    {error && <div className="alert alert-danger">{error}</div>}
                    <button className="btn btn-primary btn-block" type="submit" disabled={isSubmitting}>
                        Save
                    </button>
                </form>
                <div className="col-xs-12 col-md-2">
                    <div className="card card-flat password-reqs">
                        <div className="card-header">
                            <h6>Password Requirements</h6>
                        </div>
                        <div className="card-body">
                            <ul className="list-unstyled">
                                <li>
                                    {!valid.includes('min') && !!values.password.length && (
                                        <span className="indicator indicator-success"></span>
                                    )}{' '}
                                    Minimum 8 characters
                                </li>
                                <li>
                                    {!valid.includes('digits') && !!values.password.length && (
                                        <span className="indicator indicator-success"></span>
                                    )}{' '}
                                    Include numbers
                                </li>
                                <li>
                                    {!valid.includes('symbols') && !!values.password.length && (
                                        <span className="indicator indicator-success"></span>
                                    )}{' '}
                                    Include special character
                                </li>
                                <li>
                                    {!valid.includes('uppercase') && !!values.password.length && (
                                        <span className="indicator indicator-success"></span>
                                    )}{' '}
                                    Include uppercase letters
                                </li>
                                <li>
                                    {!valid.includes('lowercase') && !!values.password.length && (
                                        <span className="indicator indicator-success"></span>
                                    )}{' '}
                                    Include lowercase letters
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            <LoginFooter />
        </div>
    );
};

export default ResetPassword;
